<template>
   <v-layout align-start>
      <v-flex xs12 :style="$vuetify.breakpoint.smAndUp ? 'margin: 0px 2% 0px 0px' : ''">

         <v-snackbar v-model="snackbar" :color="snackColor" right bottom :timeout="4000">
            {{ snackText }}
            <template #action="{ attrs }">
               <v-btn color="white" text v-bind="attrs" @click="snackbar = false">Cerrar</v-btn>
            </template>
         </v-snackbar>

         <v-container fluid class="white mt-1">
            <v-toolbar flat color="white" class="pt-2">
               <v-toolbar-title style="font-weight:500">Facturas DTE</v-toolbar-title>
               <v-divider class="mx-2" inset vertical></v-divider>
               <v-spacer></v-spacer>
               <v-btn
                  v-if="puedeConfigurar"
                  icon
                  color="primary"
                  title="Configuración de facturación electrónica"
                  @click="abrirConfig"
               >
                  <v-icon>fas fa-cog</v-icon>
               </v-btn>
            </v-toolbar>

            <!-- FILTROS -->
            <v-layout wrap class="px-4 pb-2">
               <v-flex xs12 sm3 class="pr-2 mt-2">
                  <CustomDatePicker
                     :date="filtroFecha"
                     label="Fecha"
                     min="2015-01"
                     txt-style="width: 150px;"
                     @updateFecha="filtroFecha = $event"
                  />
               </v-flex>
               <v-flex xs12 sm3 class="pr-2">
                  <v-text-field
                     v-model="filtroCarnet"
                     label="Carnet"
                     clearable
                     color="primary"
                     @keyup.enter="listar"
                  ></v-text-field>
               </v-flex>
               <v-flex xs12 sm3 class="pr-2">
                  <v-select
                     v-model="filtroEstado"
                     :items="estadosDisponibles"
                     label="Estado DTE"
                     clearable
                     color="primary"
                     @change="listar"
                  ></v-select>
               </v-flex>
               <v-flex xs12 sm3 class="d-flex align-center">
                  <v-btn color="primary" @click="listar" :loading="loading">
                     <v-icon left>fas fa-search</v-icon> Buscar
                  </v-btn>
               </v-flex>
            </v-layout>

            <!-- TABLA -->
            <div style="overflow-x: auto;">
               <v-data-table
                  :headers="headers"
                  :items="itemsAgrupados"
                  :loading="loading"
                  loading-text="Cargando registros..."
                  :footer-props="{ itemsPerPageOptions: [10, 25, 50], itemsPerPageText: 'Filas por página:' }"
                  class="mt-2 tabla-dte"
               >
                  <template #item.concepto="{ item }">
                     <div class="d-flex align-center">
                        <span
                           :title="item.pagos.length > 1 ? item.conceptoDetalle : null"
                           :style="
                              item.pagos.length > 1 && !item.esPaqueteGraduacion
                                 ? 'max-width:220px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; display:inline-block;'
                                 : ''
                           "
                        >{{ item.concepto }}</span>
                        <v-chip v-if="item.pagos.length > 1" x-small color="primary" dark class="ml-2 flex-shrink-0">
                           {{ item.pagos.length }} pagos
                        </v-chip>
                     </div>
                  </template>

                  <template #item.monto="{ item }">
                     <div class="d-flex align-center justify-end">
                        <v-chip v-if="item.pagos.some(p => p.esBeca)" x-small color="teal" dark class="mr-2">BECA</v-chip>
                        <v-chip v-else x-small color="grey lighten-2" class="mr-2">Sin beca</v-chip>
                        <span class="text-no-wrap">$ {{ item.monto.toFixed(2) }}</span>
                     </div>
                  </template>

                  <template #item.fechaPago="{ item }">
                     <span class="text-no-wrap">{{ formatFecha(item.fechaPago) }}</span>
                  </template>

                  <template #item.numeroControl="{ item }">
                     <span class="text-no-wrap">{{ item.numeroControl }}</span>
                  </template>

                  <template #item.estado="{ item }">
                     <v-chip :color="item.estado === 'PROCESADO' ? 'success' : 'warning'" dark small>
                        {{ item.estado }}
                     </v-chip>
                  </template>

                  <template #item.detalle="{ item }">
                     <v-btn icon small color="primary" @click="verDetalle(item)">
                        <v-icon small>fas fa-eye</v-icon>
                     </v-btn>
                  </template>

                  <template #no-data>
                     <span>No hay registros. Aplica un filtro y haz clic en Buscar.</span>
                  </template>
               </v-data-table>
            </div>
         </v-container>

         <!-- DIALOG CONFIGURACIÓN DTE -->
         <v-dialog v-model="dialogConfig" max-width="640px" scrollable persistent>
            <v-card>
               <v-card-title class="text-h6 font-weight-medium py-3">
                  Configuración de facturación electrónica
               </v-card-title>
               <v-divider></v-divider>
               <v-card-text class="pt-4">
                  <p class="text-subtitle-2 font-weight-bold primary--text mb-1">
                     ¿Dónde se emite factura electrónica?
                  </p>
                  <div class="caption grey--text text--darken-1 mb-2">
                     Al desactivar un flujo, esos pagos se siguen registrando normalmente:
                     lo único que deja de ocurrir es el envío del DTE a Hacienda.
                  </div>

                  <v-checkbox
                     v-model="config.colecturiaDigital"
                     label="Colecturía Digital - Alumnos de Pregrado , Posgrado con talonario"
                     hide-details
                     dense
                     class="mt-1"
                  />
                  <v-checkbox
                     v-model="config.externaConContrato"
                     label="Colecturía Externa — Colegio Madre de la Iglesia (cuotas)"
                     hide-details
                     dense
                  />
                  <v-checkbox
                     v-model="config.externaSinContrato"
                     label="Colecturía Externa — Alumnos externos sin talonario (aranceles e idiomas)"
                     hide-details
                     dense
                  />

                  <v-divider class="my-4"></v-divider>

                  <p class="text-subtitle-2 font-weight-bold primary--text mb-1">
                     API de facturación
                  </p>
                  <v-text-field
                     v-model="config.baseUrl"
                     label="URL base"
                     placeholder="http://50.21.176.120:8980"
                     persistent-hint
                     hint="Solo el host y el puerto. La ruta /api/factura/crear la agrega el sistema."
                     :error-messages="errorUrl"
                     color="primary"
                  ></v-text-field>
               </v-card-text>
               <v-divider></v-divider>
               <v-card-actions class="py-2">
                  <v-spacer></v-spacer>
                  <v-btn text :disabled="guardandoConfig" @click="dialogConfig = false">
                     Cancelar
                  </v-btn>
                  <v-btn color="primary" :loading="guardandoConfig" @click="guardarConfig">
                     <v-icon left small>fas fa-save</v-icon> Guardar
                  </v-btn>
               </v-card-actions>
            </v-card>
         </v-dialog>

         <!-- DIALOG DETALLE -->
         <v-dialog v-model="dialogDetalle" :max-width="$vuetify.breakpoint.lgAndUp ? '1100px' : '95vw'" scrollable>
            <v-card v-if="itemSeleccionado">
               <v-card-title class="text-h6 font-weight-medium py-3">
                  Detalle de Factura DTE
                  <v-spacer></v-spacer>
                  <v-btn icon @click="dialogDetalle = false"><v-icon>fas fa-times</v-icon></v-btn>
               </v-card-title>
               <v-divider></v-divider>
               <v-card-text class="pt-4 pb-2">

                  <!-- RESPUESTA DTE -->
                  <p class="text-subtitle-1 font-weight-bold primary--text mb-2">Respuesta DTE</p>
                  <v-row dense>
                     <v-col cols="12" sm="6">
                        <v-row dense>
                           <v-col cols="5" class="font-weight-medium caption black--text">Estado</v-col>
                           <v-col cols="7">
                              <v-chip :color="itemSeleccionado.estado === 'PROCESADO' ? 'success' : 'warning'" dark x-small>
                                 {{ itemSeleccionado.estado }}
                              </v-chip>
                           </v-col>
                        </v-row>
                        <v-row dense>
                           <v-col cols="5" class="font-weight-medium caption black--text">Número control</v-col>
                           <v-col cols="7" class="caption black--text">{{ itemSeleccionado.numeroControl }}</v-col>
                        </v-row>
                        <v-row dense>
                           <v-col cols="5" class="font-weight-medium caption black--text">Fecha DTE</v-col>
                           <v-col cols="7" class="caption black--text">{{ itemSeleccionado.fechaDte }}</v-col>
                        </v-row>
                        <v-row dense>
                           <v-col cols="5" class="font-weight-medium caption black--text">Mensaje</v-col>
                           <v-col cols="7" class="caption black--text">{{ itemSeleccionado.mensaje }}</v-col>
                        </v-row>
                     </v-col>
                     <v-col cols="12" sm="6">
                        <v-row dense>
                           <v-col cols="5" class="font-weight-medium caption black--text">Código generación</v-col>
                           <v-col cols="7" class="caption black--text" style="word-break:break-all">{{ itemSeleccionado.codigoGeneracion }}</v-col>
                        </v-row>
                        <v-row dense>
                           <v-col cols="5" class="font-weight-medium caption black--text">Sello recibido</v-col>
                           <v-col cols="7" class="caption black--text" style="word-break:break-all">{{ itemSeleccionado.selloRecibido }}</v-col>
                        </v-row>
                     </v-col>
                  </v-row>

                  <v-divider class="my-3"></v-divider>

                  <!-- PAGOS INCLUIDOS -->
                  <p class="text-subtitle-1 font-weight-bold primary--text mb-2">
                     Pagos incluidos
                     <v-chip x-small color="primary" dark class="ml-2">{{ itemSeleccionado.pagos.length }}</v-chip>
                  </p>
                  <div style="overflow-x: auto;">
                     <v-simple-table dense>
                        <thead>
                           <tr>
                              <th class="text-left text-no-wrap">Carnet</th>
                              <th class="text-left text-no-wrap">Concepto</th>
                              <th v-if="itemSeleccionado.pagos.some(p => p.arancelCodigo && !(p.concepto || '').toUpperCase().includes('CUOTA'))" class="text-left text-no-wrap">Arancel</th>
                              <th class="text-center text-no-wrap">Beca</th>
                              <th class="text-right text-no-wrap">Monto</th>
                              <th class="text-left text-no-wrap">NPE</th>
                              <th class="text-left text-no-wrap">Autorización</th>
                              <th class="text-left text-no-wrap">Referencia</th>
                           </tr>
                        </thead>
                        <tbody v-for="pago in itemSeleccionado.pagos" :key="pago.pagoId">
                           <tr>
                              <td class="text-no-wrap">{{ pago.alumnoCarnet }}</td>
                              <td>{{ pago.concepto }}</td>
                              <td v-if="itemSeleccionado.pagos.some(p => p.arancelCodigo && !(p.concepto || '').toUpperCase().includes('CUOTA'))" class="text-no-wrap">{{ formatArancel(pago.arancelCodigo) }}</td>
                              <td class="text-center text-no-wrap">
                                 <v-chip x-small :color="pago.esBeca ? 'teal' : 'grey lighten-2'" :dark="pago.esBeca">
                                    {{ pago.esBeca ? 'Sí' : 'No' }}
                                 </v-chip>
                              </td>
                              <td class="text-right text-no-wrap">$ {{ Number(pago.esBeca ? pago.montoOriginal : (pago.monto - (pago.montoRecargo || 0))).toFixed(2) }}</td>
                              <td class="text-no-wrap caption">{{ pago.npe }}</td>
                              <td class="text-no-wrap">{{ pago.autorizacion }}</td>
                              <td class="text-no-wrap">{{ pago.referencia }}</td>
                           </tr>
                           <tr v-if="pago.montoRecargo" class="grey lighten-4">
                              <td></td>
                              <td class="caption grey--text text--darken-2">↳ Recargo por pago extraordinario</td>
                              <td v-if="itemSeleccionado.pagos.some(p => p.arancelCodigo && !(p.concepto || '').toUpperCase().includes('CUOTA'))" class="text-no-wrap caption grey--text text--darken-2">{{ formatArancel(pago.arancelCodigoRecargo) }}</td>
                              <td></td>
                              <td class="text-right text-no-wrap caption grey--text text--darken-2">$ {{ Number(pago.montoRecargo).toFixed(2) }}</td>
                              <td colspan="3"></td>
                           </tr>
                        </tbody>
                        <tbody>
                           <tr class="font-weight-bold">
                              <td :colspan="itemSeleccionado.pagos.some(p => p.arancelCodigo && !(p.concepto || '').toUpperCase().includes('CUOTA')) ? 4 : 3" class="text-right">Total</td>
                              <td class="text-right text-no-wrap">$ {{ itemSeleccionado.monto.toFixed(2) }}</td>
                              <td colspan="3"></td>
                           </tr>
                        </tbody>
                     </v-simple-table>
                  </div>

               </v-card-text>
               <v-divider></v-divider>
               <v-card-actions class="py-2">
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="dialogDetalle = false">Cerrar</v-btn>
               </v-card-actions>
            </v-card>
         </v-dialog>

      </v-flex>
   </v-layout>
</template>

<script>
import axios from 'axios'
import { authComputed } from '../helpers'
const CustomDatePicker = () => import('./custom/CustomDatePicker')

// Paquetes de graduación. El backend crea un Pago por cada arancel del paquete, así
// que en la tabla llegan como varios pagos bajo un mismo código de generación y el
// concepto quedaría como una concatenación larga e ilegible. Acá se reconoce el
// paquete por sus códigos y se muestra un nombre único.
//
// El depósito por alquiler de togas (02-998) NO se lista a propósito: no se factura,
// así que nunca viene en el DTE. Exigirlo haría que ningún paquete se detecte.
//
// Pregrado y maestría solo se diferencian por el primer código (01-020 vs 02-062);
// el resto lo comparten. Por eso se compara el conjunto completo y no un código suelto.
const PAQUETES_GRADUACION = [
   {
      nombre: 'ARANCELES DE GRADUACIÓN POSGRADO DOCTORADO',
      codigos: ['02-077', '01-058', '02-017', '03-051', '02-087']
   },
   {
      nombre: 'ARANCELES DE GRADUACIÓN - MAESTRIA',
      codigos: ['02-062', '01-056', '02-010', '03-050']
   },
   {
      nombre: 'ARANCELES DE GRADUACIÓN - PREGRADO',
      codigos: ['01-020', '01-056', '02-010', '03-050']
   }
]

export default {
   name: 'PagosDte',
   components: {
      CustomDatePicker
   },
   data: () => ({
      loading: false,
      // Configuración de facturación electrónica (permiso 32)
      dialogConfig: false,
      guardandoConfig: false,
      errorUrl: '',
      config: {
         colecturiaDigital: false,
         externaConContrato: false,
         externaSinContrato: false,
         baseUrl: ''
      },
      snackbar: false,
      snackColor: 'error',
      snackText: '',
      filtroFecha: null,
      filtroCarnet: '',
      filtroEstado: null,
      dialogDetalle: false,
      itemSeleccionado: null,
      items: [],
      headers: [
         { text: 'Carnet',          value: 'alumnoCarnet',  class: 'titulo' },
         { text: 'Concepto',        value: 'concepto',      class: 'titulo' },
         { text: 'Monto',           value: 'monto',         class: 'titulo', align: 'right' },
         { text: 'Fecha pago',      value: 'fechaPago',     class: 'titulo' },
         { text: 'Número control',  value: 'numeroControl', class: 'titulo' },
         { text: 'Estado DTE',      value: 'estado',        class: 'titulo', align: 'center' },
         { text: 'Detalle',         value: 'detalle',       class: 'titulo', sortable: false, align: 'center' }
      ]
   }),
   computed: {
      ...authComputed,
      // La tuerca solo se muestra con el permiso 32 (Configuración DTE)
      puedeConfigurar() {
         return this.permController(31, 'ver')
      },
      estadosDisponibles() {
         return [...new Set(this.items.map(i => i.estado).filter(Boolean))].sort()
      },
      itemsAgrupados() {
         const grupos = {}

         this.items.forEach(item => {
            const key = item.codigoGeneracion
            if (!grupos[key]) {
               grupos[key] = {
                  codigoGeneracion: item.codigoGeneracion,
                  numeroControl:    item.numeroControl,
                  selloRecibido:    item.selloRecibido,
                  fechaDte:         item.fechaDte,
                  estado:           item.estado,
                  mensaje:          item.mensaje,
                  alumnoCarnet:     item.alumnoCarnet,
                  fechaPago:        item.fechaPago,
                  pagos:            []
               }
            }
            grupos[key].pagos.push(item)
         })

         return Object.values(grupos).map(grupo => {
            const monto = grupo.pagos.reduce((sum, p) => sum + Number(p.esBeca ? p.montoOriginal : p.monto), 0)

            // Detalle de los conceptos individuales: se conserva para el tooltip,
            // porque al mostrar el nombre del paquete se perdería esa información.
            const conceptoDetalle = grupo.pagos.length === 1
               ? grupo.pagos[0].concepto
               : grupo.pagos.map(p => p.concepto).join(' + ')

            const paquete = this.detectarPaqueteGraduacion(grupo.pagos)
            const concepto = paquete || conceptoDetalle

            // Los paquetes se muestran completos: el nombre es corto y fijo, así que
            // recortarlo con puntos suspensivos solo estorbaría. El recorte se mantiene
            // para los grupos comunes, donde el concepto es una concatenación larga.
            return {
               ...grupo,
               monto,
               concepto,
               conceptoDetalle,
               esPaqueteGraduacion: !!paquete
            }
         })
      }
   },
   mounted() {
      const hoy = new Date()
      const yyyy = hoy.getFullYear()
      const mm = String(hoy.getMonth() + 1).padStart(2, '0')
      const dd = String(hoy.getDate()).padStart(2, '0')
      this.filtroFecha = `${yyyy}-${mm}-${dd}`
   },
   watch: {
      filtroFecha() {
         this.listar()
      }
   },
   methods: {
      listar() {
         this.loading = true
         const params = {}
         if (this.filtroFecha)  params.fecha  = this.filtroFecha
         if (this.filtroCarnet) params.carnet = this.filtroCarnet.trim().toUpperCase()
         if (this.filtroEstado) params.estado = this.filtroEstado

         axios.get('api/DteRespuesta', { params })
            .then((res) => { this.items = res.data })
            .catch(() => { this.showSnack('Error al cargar los registros DTE') })
            .finally(() => { this.loading = false })
      },
      verDetalle(item) {
         this.itemSeleccionado = item
         this.dialogDetalle = true
      },
      abrirConfig() {
         this.errorUrl = ''
         axios
            .get('api/DteConfiguracion')
            .then((res) => {
               this.config = {
                  colecturiaDigital: !!res.data.colecturiaDigital,
                  externaConContrato: !!res.data.externaConContrato,
                  externaSinContrato: !!res.data.externaSinContrato,
                  baseUrl: res.data.baseUrl || ''
               }
               this.dialogConfig = true
            })
            .catch(() => {
               this.showSnack(
                  'No se pudo cargar la configuración. Verifique que se haya ejecutado DteConfiguracion.sql.'
               )
            })
      },
      guardarConfig() {
         const url = (this.config.baseUrl || '').trim()
         if (!/^https?:\/\/.+/i.test(url)) {
            this.errorUrl = 'Debe ser una URL absoluta que inicie con http:// o https://'
            return
         }
         this.errorUrl = ''
         this.guardandoConfig = true

         axios
            .put('api/DteConfiguracion', {
               colecturiaDigital: this.config.colecturiaDigital,
               externaConContrato: this.config.externaConContrato,
               externaSinContrato: this.config.externaSinContrato,
               baseUrl: url
            })
            .then(() => {
               this.showSnack('Configuración guardada.', 'success')
               this.dialogConfig = false
            })
            .catch((error) => {
               const msg =
                  error.response && typeof error.response.data === 'string'
                     ? error.response.data
                     : 'No se pudo guardar la configuración.'
               this.showSnack(msg)
            })
            .finally(() => {
               this.guardandoConfig = false
            })
      },
      formatFecha(fecha) {
         if (!fecha) return ''
         return new Date(fecha).toLocaleDateString('es-SV', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
         })
      },
      /**
       * Devuelve el nombre del paquete de graduación si los pagos del grupo cubren
       * todos sus aranceles, o null si no corresponde a ninguno.
       *
       * Se usa "los contiene a todos" y no una igualdad exacta para que el paquete
       * se siga reconociendo si el DTE trae algún ítem extra (por ejemplo una línea
       * de recargo). Los tres paquetes se distinguen por códigos propios, así que
       * no hay riesgo de que uno matchee por otro.
       */
      detectarPaqueteGraduacion(pagos) {
         const codigos = new Set(
            (pagos || [])
               .map((p) => this.formatArancel(p.arancelCodigo))
               .filter(Boolean)
         )
         if (!codigos.size) return null

         const paquete = PAQUETES_GRADUACION.find((p) =>
            p.codigos.every((c) => codigos.has(c))
         )
         return paquete ? paquete.nombre : null
      },
      formatArancel(codigo) {
         if (!codigo) return ''
         const partes = codigo.split('-')
         return partes.length >= 2 ? `${partes[0]}-${partes[1]}` : codigo
      },
      showSnack(text, color = 'error') {
         this.snackColor = color
         this.snackText = text
         this.snackbar = true
      }
   }
}
</script>

<style>
.tabla-dte td,
.tabla-dte th {
   padding-left: 8px !important;
   padding-right: 8px !important;
}
</style>
